import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import { Menu, X, Globe, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('home', translations.nav) },
    { path: '/services', label: t('services', translations.nav) },
    { path: '/about', label: t('about', translations.nav) },
    { path: '/contact', label: t('contact', translations.nav) },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'glass bg-[#0f1419]/90 border-b border-gray-800/50' 
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">N</span>
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold text-lg tracking-tight">NATCAS</span>
              <span className="text-gray-500 text-[10px] font-medium tracking-[0.2em] uppercase">Group</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`relative text-sm font-medium tracking-wide transition-colors accent-line pb-1 ${
                  isActive(link.path)
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center gap-2 text-xs font-mono text-gray-400 hover:text-white transition-colors"
            >
              <Globe className="w-4 h-4" strokeWidth={1.5} />
              <span>{language === 'en' ? 'SE' : 'EN'}</span>
            </button>

            <Button
              asChild
              className="hidden lg:flex bg-blue-600 hover:bg-blue-500 text-white rounded-none px-6 h-10 text-sm font-semibold"
              data-testid="header-cta"
            >
              <Link to="/contact">
                {t('button', translations.cta)}
              </Link>
            </Button>

            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden absolute top-full left-0 right-0 bg-[#0f1419] border-t border-gray-800 border-b border-gray-800 shadow-2xl"
              data-testid="mobile-nav"
            >
              <div className="px-6 py-6 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                    className={`block py-4 text-lg font-medium border-b border-gray-800/50 ${
                      isActive(link.path) ? 'text-blue-400' : 'text-gray-300'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Button
                  asChild
                  className="mt-6 w-full bg-blue-600 hover:bg-blue-500 text-white rounded-none h-14 font-semibold"
                >
                  <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                    {t('button', translations.cta)}
                  </Link>
                </Button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
